import React, { Suspense, lazy, useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import { StoreProvider, useStore } from "@/context/StoreContext";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Products from "@/components/Products";
import Feature from "@/components/Feature";
import Story from "@/components/Story";
import Support from "@/components/Support";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import CheckoutModal from "@/components/CheckoutModal";
import MobileMenu from "@/components/MobileMenu";
import Toast from "@/components/Toast";
import AuthModal from "@/components/AuthModal";
import FaqWidget from "@/components/FaqWidget";

const TrackPage = lazy(() => import("@/components/TrackPage"));
const MyOrders = lazy(() => import("@/components/MyOrders"));
const OwnerOrders = lazy(() => import("@/components/OwnerOrders"));
const MembershipPage = lazy(() => import("@/components/MembershipPage"));

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  render() {
    if (this.state.err) {
      return <div style={{ padding: "120px 24px", textAlign: "center", fontFamily: "sans-serif" }}>Something went wrong. Please refresh.</div>;
    }
    return this.props.children;
  }
}

function Shell() {
  const { cartOpen, menuOpen, checkoutOpen, authOpen, closeAll } = useStore();
  const anyOpen = cartOpen || menuOpen || checkoutOpen;

  useEffect(() => {
    document.body.classList.toggle("menu-open", anyOpen);
  }, [anyOpen]);

  return (
    <>
      <Nav />
      <MobileMenu />
      <main>
        <Hero />
        <Products />
        <Marquee />
        <Feature />
        <Story />
        <Support />
      </main>
      <Footer />
      <CartDrawer />
      <CheckoutModal />
      <Toast />
      <div
        className={`overlay ${anyOpen ? "show" : ""}`}
        onClick={closeAll}
        data-testid="overlay"
      />
    </>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <StoreProvider>
        <BrowserRouter>
          <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
            <Routes>
              <Route path="/track" element={<TrackPage />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/membership" element={<MembershipPage />} />
              <Route path="/owner" element={<OwnerOrders />} />
              <Route path="*" element={<Shell />} />
            </Routes>
          </Suspense>
          <FaqWidget />
          <AuthModal />
        </BrowserRouter>
      </StoreProvider>
    </ErrorBoundary>
  );
}
